import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { CloudUpload, Delete, Add } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_OPTIONS = [
  { value: 'A', label: 'A − Good and in use' },
  { value: 'B', label: 'B − Good but not in use' },
  { value: 'C', label: 'C − In use but needs repair' },
  { value: 'D', label: 'D − In use but needs maintenance' },
  { value: 'E', label: 'E − Not in use but needs repair and maintenance' },
  { value: 'F', label: 'F − Not functional, out of use and needs to be discarded' },
]

const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const initialFormData = {
  name: '',
  category_id: '',
  asset_tag: '',
  department_id: '',
  manufacturer_id: '',
  model_number: '',
  serial_number: '',
  engraved_no: '',  // Equipment engraved/asset number (UCI Job Card)
  location_id: '',
  room_number: '',
  status: 'A',
  criticality: 'medium',
  facility_id: '',
  unit_count: 1,
  purchase_date: '',
  purchase_cost: '',
  warranty_expiry: '',
  expected_life_years: '',
  // New fields from client requirements
  manufacturing_date: '',
  country_of_origin: '',
  supplier_id: '',
  received_by_id: '',
  received_date: '',
  delivered_by: '',
  notes: '',  // Remarks field
}

const AssetForm = ({ open, onClose, asset, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])
  const [manufacturers, setManufacturers] = useState([])
  const [locations, setLocations] = useState([])
  const [facilities, setFacilities] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [users, setUsers] = useState([])

  // Image upload state
  const [imageFiles, setImageFiles] = useState({ image: null, image_2: null, image_3: null, qr_code: null })
  const [imagePreviews, setImagePreviews] = useState({ image: null, image_2: null, image_3: null, qr_code: null })

  // Quick-add modal states
  const [quickAddType, setQuickAddType] = useState(null) // 'category', 'manufacturer', 'location', 'facility'
  const [quickAddData, setQuickAddData] = useState({})
  const [quickAddLoading, setQuickAddLoading] = useState(false)

  const isEditing = Boolean(asset)

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (asset) {
        setFormData({
          name: asset.name || '',
          category_id: asset.category?.id || '',
          asset_tag: asset.asset_tag || '',
          department_id: asset.department || '',
          manufacturer_id: asset.manufacturer?.id || '',
          model_number: asset.model_number || '',
          serial_number: asset.serial_number || '',
          engraved_no: asset.engraved_no || '',
          location_id: asset.location?.id || '',
          room_number: asset.room_number || '',
          status: asset.status || 'A',
          criticality: asset.criticality || 'medium',
          facility_id: asset.facility || '',
          unit_count: asset.unit_count || 1,
          purchase_date: asset.purchase_date || '',
          purchase_cost: asset.purchase_cost || '',
          warranty_expiry: asset.warranty_expiry || '',
          expected_life_years: asset.expected_life_years || '',
          // New fields
          manufacturing_date: asset.manufacturing_date || '',
          country_of_origin: asset.country_of_origin || '',
          supplier_id: asset.supplier || '',
          received_by_id: asset.received_by || '',
          received_date: asset.received_date || '',
          delivered_by: asset.delivered_by || '',
          notes: asset.notes || '',
        })
        // Set existing image previews
        setImagePreviews({
          image: asset.image || null,
          image_2: asset.image_2 || null,
          image_3: asset.image_3 || null,
          qr_code: asset.qr_code || null,
        })
        setImageFiles({ image: null, image_2: null, image_3: null, qr_code: null })
      } else {
        setFormData(initialFormData)
        setImagePreviews({ image: null, image_2: null, image_3: null, qr_code: null })
        setImageFiles({ image: null, image_2: null, image_3: null, qr_code: null })
      }
      setErrors({})
    }
  }, [open, asset])

  const fetchOptions = async () => {
    try {
      const [catRes, deptRes, mfgRes, locRes, facilityRes, supplierRes, usersRes] = await Promise.all([
        api.get('/assets/categories/'),
        api.get('/common/departments/'),
        api.get('/assets/manufacturers/'),
        api.get('/assets/locations/'),
        api.get('/assets/facilities/'),
        api.get('/vendors/'),
        api.get('/common/users/'),
      ])
      // Categories are dynamically loaded from the database (ME - Medical Equipment, MF - Medical Furniture)
      setCategories(catRes.data.results || catRes.data)
      setDepartments(deptRes.data.results || deptRes.data)
      setManufacturers(mfgRes.data.results || mfgRes.data)
      setLocations(locRes.data.results || locRes.data)
      setFacilities(facilityRes.data.results || facilityRes.data)
      setSuppliers(supplierRes.data.results || supplierRes.data)
      setUsers(usersRes.data.results || usersRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0]
    if (file) {
      setImageFiles(prev => ({ ...prev, [fieldName]: file }))
      setImagePreviews(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) }))
    }
  }

  const handleRemoveImage = (fieldName) => {
    setImageFiles(prev => ({ ...prev, [fieldName]: null }))
    setImagePreviews(prev => ({ ...prev, [fieldName]: null }))
  }

  // Quick-add handlers
  const openQuickAdd = (type) => {
    const initialData = {
      category: { name: '', code: '', description: '', is_active: true },
      manufacturer: { name: '', contact_info: '', website: '', is_active: true },
      location: { name: '', building: '', floor: '', ward: '', department: '', is_active: true },
      facility: { name: '', address: '', contact_phone: '', contact_email: '', is_active: true },
    }
    setQuickAddType(type)
    setQuickAddData(initialData[type] || {})
  }

  const closeQuickAdd = () => {
    setQuickAddType(null)
    setQuickAddData({})
  }

  const handleQuickAddSubmit = async () => {
    const endpoints = {
      category: '/assets/categories/',
      manufacturer: '/assets/manufacturers/',
      location: '/assets/locations/',
      facility: '/assets/facilities/',
    }

    // Validate required fields
    if (!quickAddData.name) {
      toast.error('Name is required')
      return
    }
    if (quickAddType === 'category' && !quickAddData.code) {
      toast.error('Code is required')
      return
    }

    setQuickAddLoading(true)
    try {
      const payload = { ...quickAddData }
      if (quickAddType === 'location' && !payload.department) {
        payload.department = null
      }

      const response = await api.post(endpoints[quickAddType], payload)
      const newItem = response.data

      // Update the appropriate list and select the new item
      switch (quickAddType) {
        case 'category':
          setCategories(prev => [...prev, newItem])
          setFormData(prev => ({ ...prev, category_id: newItem.id }))
          break
        case 'manufacturer':
          setManufacturers(prev => [...prev, newItem])
          setFormData(prev => ({ ...prev, manufacturer_id: newItem.id }))
          break
        case 'location':
          setLocations(prev => [...prev, newItem])
          setFormData(prev => ({ ...prev, location_id: newItem.id }))
          break
        case 'facility':
          setFacilities(prev => [...prev, newItem])
          setFormData(prev => ({ ...prev, facility_id: newItem.id }))
          break
      }

      toast.success(`${quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1)} created successfully`)
      closeQuickAdd()
    } catch (error) {
      console.error('Error creating item:', error)
      toast.error(error.response?.data?.detail || `Failed to create ${quickAddType}`)
    } finally {
      setQuickAddLoading(false)
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.category_id) newErrors.category_id = 'Category is required'
    // Asset tag validation - must be exactly 15 characters if provided
    if (formData.asset_tag && formData.asset_tag.length !== 15) {
      newErrors.asset_tag = `Asset tag must be exactly 15 characters. Currently ${formData.asset_tag.length} characters.`
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      // Use FormData for multipart/form-data (required for image uploads)
      const submitData = new FormData()

      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          // Handle field name mapping
          if (key === 'supplier_id') {
            submitData.append('supplier', value)
          } else if (key === 'received_by_id') {
            submitData.append('received_by', value)
          } else if (key === 'department_id') {
            submitData.append('department', value)
          } else {
            submitData.append(key, value)
          }
        }
      })

      // Add image files if selected
      if (imageFiles.image) {
        submitData.append('image', imageFiles.image)
      }
      if (imageFiles.image_2) {
        submitData.append('image_2', imageFiles.image_2)
      }
      if (imageFiles.image_3) {
        submitData.append('image_3', imageFiles.image_3)
      }
      if (imageFiles.qr_code) {
        submitData.append('qr_code', imageFiles.qr_code)
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      if (isEditing) {
        await api.patch(`/assets/assets/${asset.id}/`, submitData, config)
      } else {
        await api.post('/assets/assets/', submitData, config)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving asset:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Asset' : 'Add New Asset'}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Asset Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Asset Tag Number"
            name="asset_tag"
            value={formData.asset_tag}
            onChange={handleChange}
            error={Boolean(errors.asset_tag)}
            helperText={errors.asset_tag || 'Must be exactly 15 characters. Leave blank for auto-generation.'}
            inputProps={{ maxLength: 15 }}
            placeholder="e.g., MEDEQ0000000001"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth error={Boolean(errors.category_id)} required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                label="Category"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add new category">
              <IconButton
                onClick={() => openQuickAdd('category')}
                sx={{ mt: 1, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                size="small"
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Section/Unit</InputLabel>
            <Select
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              label="Section/Unit"
            >
              <MenuItem value="">None</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                name="manufacturer_id"
                value={formData.manufacturer_id}
                onChange={handleChange}
                label="Manufacturer"
              >
                <MenuItem value="">None</MenuItem>
                {manufacturers.map((mfg) => (
                  <MenuItem key={mfg.id} value={mfg.id}>
                    {mfg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add new manufacturer">
              <IconButton
                onClick={() => openQuickAdd('manufacturer')}
                sx={{ mt: 1, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                size="small"
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Model Number"
            name="model_number"
            value={formData.model_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Serial Number"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Engraved No / Asset Number"
            name="engraved_no"
            value={formData.engraved_no}
            onChange={handleChange}
            helperText="Equipment engraved/asset number (for UCI Job Card)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                name="location_id"
                value={formData.location_id}
                onChange={handleChange}
                label="Location"
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} {loc.building && `- ${loc.building}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add new location">
              <IconButton
                onClick={() => openQuickAdd('location')}
                sx={{ mt: 1, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                size="small"
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Room Number"
            name="room_number"
            value={formData.room_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Criticality</InputLabel>
            <Select
              name="criticality"
              value={formData.criticality}
              onChange={handleChange}
              label="Criticality"
            >
              {CRITICALITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select
                name="facility_id"
                value={formData.facility_id}
                onChange={handleChange}
                label="Facility"
              >
                <MenuItem value="">
                  <em>Select Facility</em>
                </MenuItem>
                {facilities.filter(f => f.is_active).map((facility) => (
                  <MenuItem key={facility.id} value={facility.id}>
                    {facility.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add new facility">
              <IconButton
                onClick={() => openQuickAdd('facility')}
                sx={{ mt: 1, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                size="small"
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Number of Units"
            name="unit_count"
            type="number"
            value={formData.unit_count}
            onChange={handleChange}
            inputProps={{ min: 1 }}
            helperText="Number of identical units of this equipment"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Purchase Date"
            name="purchase_date"
            type="date"
            value={formData.purchase_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Purchase Cost"
            name="purchase_cost"
            type="number"
            value={formData.purchase_cost}
            onChange={handleChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Warranty Expiry"
            name="warranty_expiry"
            type="date"
            value={formData.warranty_expiry}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Expected Life (Years)"
            name="expected_life_years"
            type="number"
            value={formData.expected_life_years}
            onChange={handleChange}
          />
        </Grid>

        {/* Manufacturing & Origin Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Manufacturing & Origin
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Manufacturing Date"
            name="manufacturing_date"
            type="date"
            value={formData.manufacturing_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country of Origin"
            name="country_of_origin"
            value={formData.country_of_origin}
            onChange={handleChange}
          />
        </Grid>

        {/* Receiving Information Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Receiving Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Supplier</InputLabel>
            <Select
              name="supplier_id"
              value={formData.supplier_id}
              onChange={handleChange}
              label="Supplier"
            >
              <MenuItem value="">None</MenuItem>
              {suppliers.map((sup) => (
                <MenuItem key={sup.id} value={sup.id}>
                  {sup.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Delivered By"
            name="delivered_by"
            value={formData.delivered_by}
            onChange={handleChange}
            placeholder="Name of delivery person"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Received By</InputLabel>
            <Select
              name="received_by_id"
              value={formData.received_by_id}
              onChange={handleChange}
              label="Received By"
            >
              <MenuItem value="">None</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Received Date"
            name="received_date"
            type="date"
            value={formData.received_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Images Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Asset Images
          </Typography>
        </Grid>

        {['image', 'image_2', 'image_3'].map((fieldName, index) => (
          <Grid item xs={12} md={4} key={fieldName}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                position: 'relative',
                minHeight: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {imagePreviews[fieldName] ? (
                <>
                  <Box
                    component="img"
                    src={imagePreviews[fieldName]}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 100,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(fieldName)}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {index === 0 ? 'Primary Image' : `Image ${index + 1}`}
                  </Typography>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, fieldName)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
            </Box>
          </Grid>
        ))}

        {/* QR Code Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            QR Code (Optional - will be auto-generated if not provided)
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              position: 'relative',
              minHeight: 140,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {imagePreviews.qr_code ? (
              <>
                <Box
                  component="img"
                  src={imagePreviews.qr_code}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 100,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage('qr_code')}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Upload QR Code
                </Typography>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, 'qr_code')}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Remarks"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Modal>

    {/* Quick-Add Modal */}
    <Modal
      open={Boolean(quickAddType)}
      onClose={closeQuickAdd}
      title={`Add New ${quickAddType ? quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1) : ''}`}
      maxWidth="sm"
      actions={
        <>
          <Button variant="outlined" onClick={closeQuickAdd}>Cancel</Button>
          <Button onClick={handleQuickAddSubmit} loading={quickAddLoading}>Create</Button>
        </>
      }
    >
      <Grid container spacing={2}>
        {/* Category Form */}
        {quickAddType === 'category' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                value={quickAddData.code || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, code: e.target.value })}
                required
                helperText="e.g., MED-EQUIP"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={quickAddData.name || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={quickAddData.description || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </>
        )}

        {/* Manufacturer Form */}
        {quickAddType === 'manufacturer' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={quickAddData.name || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Info"
                value={quickAddData.contact_info || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, contact_info: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={quickAddData.website || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, website: e.target.value })}
                placeholder="https://example.com"
              />
            </Grid>
          </>
        )}

        {/* Location Form */}
        {quickAddType === 'location' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={quickAddData.name || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Building"
                value={quickAddData.building || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, building: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Floor"
                value={quickAddData.floor || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, floor: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ward"
                value={quickAddData.ward || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, ward: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={quickAddData.department || ''}
                  onChange={(e) => setQuickAddData({ ...quickAddData, department: e.target.value })}
                  label="Department"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* Facility Form */}
        {quickAddType === 'facility' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={quickAddData.name || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={quickAddData.address || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={quickAddData.contact_phone || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={quickAddData.contact_email || ''}
                onChange={(e) => setQuickAddData({ ...quickAddData, contact_email: e.target.value })}
              />
            </Grid>
          </>
        )}

        {/* Active switch for all types */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={quickAddData.is_active !== false}
                onChange={(e) => setQuickAddData({ ...quickAddData, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Grid>
      </Grid>
    </Modal>
    </>
  )
}

export default AssetForm
